import { getPlatformName } from '@/actions/platform'
import { Countdown } from '@/components/_event/general/countdown'
import DonationCampaignCta from '@/components/_registration/donation-campaign-cta'
import LoginButton from '@/components/_registration/login-button'
import LocalizedDateTime from '@/components/common/localized-date-time'
import ArrowLeft from '@/components/icons/arrow-left'
import ArrowNoTail from '@/components/icons/arrow-no-tail'
import ArrowRight from '@/components/icons/arrow-right'
import CalendarIcon2 from '@/components/icons/calendar-icon-2'
import CarIcon from '@/components/icons/car-icon'
import ControllerIcon from '@/components/icons/controller-icon'
import HelmetIcon from '@/components/icons/helmet-icon'
import RouteIcon from '@/components/icons/route-icon'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { getChampionshipEventDriverStats } from '@/helpers/championship'
import { getFirstNonPracticeSession } from '@/helpers/events'
import dayjs from '@/lib/dayjs'
import { getDonationCampaign } from '@/lib/donation-campaigns'
import { resolveEventImageUrl } from '@/lib/racing-images'
import { cn } from '@/lib/utils'
import Image from 'next/image'
import Link from 'next/link'
import { useEffect, useMemo, useState } from 'react'
import FrontEndBreadcrumbs from '../../components/front-end-breadcrumbs'
import EventAttendeeStatusButton from './event-attendee-status-button'

export default function EventHeader({
  event,
  isFinished,
  params,
  session,
  eventDrivers,
  eventDriversLoading,
  mutateEventDrivers,
}) {
  const platformDisplayName = getPlatformName(event.championship.platforms)
  const donationCampaign = getDonationCampaign({
    eventId: event.id,
    championshipId: event.championshipId,
  })
  const eventImageUrl = resolveEventImageUrl(event)

  const prevEvent = useMemo(() => {
    if (event.championship.isSingleEvent || event.eventRoundNumber <= 1)
      return null
    return event.championship.events[event.eventRoundNumber - 2]
  }, [
    event.championship.isSingleEvent,
    event.eventRoundNumber,
    event.championship.events,
  ])

  const nextEvent = useMemo(() => {
    if (
      event.championship.isSingleEvent ||
      event.eventRoundNumber >= event.championship.totalEventCount
    )
      return null
    return event.championship.events[event.eventRoundNumber]
  }, [
    event.championship.isSingleEvent,
    event.eventRoundNumber,
    event.championship.totalEventCount,
    event.championship.events,
  ])

  return (
    <div className="w-full font-inter">
      <div className="divide h-px bg-neutral-blue-700" />

      <div className="screen-grid *:grid-indent h-[53px] desktop-xs:h-[67px]">
        <FrontEndBreadcrumbs />
      </div>

      <div className="divide h-px bg-neutral-blue-600 desktop-xs:hidden" />

      <div className="screen-grid *:grid-indent bg-linear-to-t relative overflow-hidden rounded-b-[20px] from-neutral-blue-800 to-neutral-blue-900 pb-[30px] pt-4 tablet:rounded-[40px] desktop-xs:pb-[50px] desktop-xs:pt-[21px] desktop-sm:rounded-b-[60px]">
        {/* Parent logo img and Name */}
        <div className="flex items-center gap-2 align-middle">
          <div className="relative size-7 overflow-hidden rounded-sm bg-neutral-blue-100/20 desktop-xs:size-10">
            <Image
              alt=""
              className="object-cover"
              src={
                event.championship.headerImgUrl ||
                event.championship.community.logoImgUrl ||
                '/img/default/header.webp'
              }
              fill
            />
          </div>
          {event.championship.isSingleEvent ? (
            <div>
              <span className="text-p-1 font-semibold text-neutral-blue-200">
                Hosted by{' '}
              </span>
              <Link
                href={`/c/${params.communitySlug}`}
                className="inline-block text-p-1 font-semibold text-shades-0 underline underline-offset-2"
              >
                {event.championship.community.name}
              </Link>
            </div>
          ) : (
            <Link
              href={`/c/${params.communitySlug}/${params.championshipSlug}`}
              className="text-p-1 font-semibold text-neutral-blue-200"
            >
              {event.championship.name}
            </Link>
          )}
        </div>

        <div className="divide mt-4 h-px bg-neutral-blue-600 desktop-xs:mt-[29px] desktop-xs:bg-neutral-blue-700" />
        {/* Grid */}
        <div className="grid grid-cols-4 desktop-xs:grid-cols-8 desktop-xs:gap-x-12 desktop-sm:grid-cols-10 desktop-sm:gap-x-6 desktop-md:gap-x-7 desktop-lg:gap-x-8">
          {/* Event round numbers - Full row width */}
          {!event.championship.isSingleEvent && (
            <>
              <div className="col-span-full text-p-1 font-semibold text-neutral-blue-300 desktop-xs:mt-3 desktop-xs:text-p-1">
                {prevEvent && (
                  <Link
                    href={`/c/${event.championship.community.slug}/${event.championship.slug}/${prevEvent.id}`}
                    className="hover:border-neutral-blue-700! hover:bg-neutral-blue-700! float-left flex max-w-[296px] gap-1 truncate rounded-sm border border-neutral-blue-700 bg-neutral-blue-800 p-[10px] transition-all duration-300 ease-in-out"
                  >
                    <ArrowLeft className="m-auto inline size-4 stroke-[#91A3B9] stroke-[5px] align-middle" />
                    <p>
                      Event: {event.eventRoundNumber - 1} {prevEvent.name}
                    </p>
                  </Link>
                )}
                {nextEvent && (
                  <Link
                    href={`/c/${event.championship.community.slug}/${event.championship.slug}/${nextEvent.id}`}
                    className="hover:border-neutral-blue-700! hover:bg-neutral-blue-700! float-right flex gap-1 rounded-sm border border-neutral-blue-700 bg-neutral-blue-800 p-[10px] text-right transition-all duration-300 ease-in-out"
                  >
                    <p className="max-w-[280px] truncate">
                      Event: {event.eventRoundNumber + 1} {nextEvent.name}
                    </p>
                    <ArrowRight className="m-auto inline size-4 stroke-[#91A3B9] stroke-[5px] align-middle" />
                  </Link>
                )}
              </div>
              <p className="col-span-full mt-9 text-p-2 font-semibold text-neutral-blue-200 desktop-xs:mt-9 desktop-xs:text-p-2">
                Event {event.eventRoundNumber} of{' '}
                {event.championship.totalEventCount}{' '}
              </p>
            </>
          )}

          {/* Event name - Full row width */}
          <div
            className={cn(
              'col-span-full flex items-center gap-3 align-middle desktop-xs:gap-[26px]',
              {
                'mt-[29px] desktop-xs:mt-8': event.championship.isSingleEvent,
                'mt-3 desktop-xs:mt-[14px]': !event.championship.isSingleEvent,
              },
            )}
          >
            <h1 className="truncate-2 text-h-4 font-semibold desktop-xs:text-h-1 desktop-xs:font-bold">
              {event.name}
            </h1>
            {event.championship.isSingleEvent ? (
              <div className="flex items-center gap-1 rounded-full bg-[#EEF2FF] px-4 py-2 align-middle">
                <ArrowNoTail className="size-5 stroke-[#DA0EBC] stroke-[2.5px]" />
                <p className="text-nowrap text-p-1 font-semibold text-[#DA0EBC]">
                  Single event
                </p>
              </div>
            ) : (
              !isFinished && (
                <div className="flex items-center gap-1 rounded-full bg-[#EEF2FF] px-4 py-2 align-middle">
                  <p className="text-nowrap text-p-1 font-semibold text-[#484BD0]">
                    Up next
                  </p>
                </div>
              )
            )}
          </div>

          <div className="col-span-full w-fit">
            <Countdown
              events={[event]}
              labelText="Event starts in"
              countdownClassName="text-nowrap text-p-1! font-normal! text-neutral-blue-300!"
              liveContainerClassName="mt-1 relative h-5 text-center font-bold"
              onlyRenderIfLive={true}
            />
          </div>

          {/* Event Info - Left side (on Desktop) */}
          <div className="col-span-full desktop-xs:col-span-4">
            {/* Game and Platform */}
            <div className="mt-3 flex h-fit items-center">
              <ControllerIcon className="size-7 stroke-white" />
              <div className="ml-2 text-p-1 font-semibold">
                {event.championship.game.nicename}
                {event.championship.platforms.length > 0 && (
                  <>
                    {' - '}
                    {platformDisplayName !== 'Multi-Platform' ? (
                      platformDisplayName
                    ) : (
                      <TooltipProvider>
                        <Tooltip delayDuration={0}>
                          <TooltipTrigger asChild>
                            <span className="w-fit cursor-pointer">
                              Multi-Platform (
                              {event.championship.platforms.length})
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            <div className="max-h-[300px] overflow-y-auto">
                              {event.championship.platforms.map((platform) => (
                                <div
                                  key={`platform-${platform.platform.name}`}
                                  className="rounded-sm px-2 py-2 text-p-1 font-semibold text-shades-0 transition-colors hover:bg-neutral-blue-700"
                                >
                                  {platform.platform.nicename}
                                </div>
                              ))}
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Img */}
            <div className="relative mt-[15px] aspect-video w-full overflow-hidden rounded-md bg-neutral-blue-100/20 desktop-xs:hidden">
              <Image
                alt="Event cover image"
                className="object-cover"
                src={eventImageUrl}
                height={1080}
                width={1920}
              />
            </div>

            {/* Event Stats */}
            <div
              className={cn(
                'divide mb-3 mt-4 h-px bg-neutral-blue-700 desktop-xs:mb-4',
                {
                  'desktop-xs:mt-4': event.championship.splits.length > 0,
                  'desktop-xs:mt-7': event.championship.splits.length === 0,
                },
              )}
            />

            <EventStartDate
              isFinished={isFinished}
              startDateTime={event.startDateTime}
              finishDateTime={event.finishDateTime}
              targetTimezone={event.targetTimezone}
            />

            <div className="divide my-3 h-px bg-neutral-blue-700 desktop-xs:my-4" />

            <EventTrack {...{ event }} />

            <div className="divide my-3 h-px bg-neutral-blue-700 desktop-xs:my-4" />

            <EventCarClass {...{ event }} />

            <div className="divide my-3 h-px bg-neutral-blue-700 desktop-xs:my-4" />

            <EventRegisteredDrivers
              {...{
                event,
                eventDrivers,
                eventDriversLoading,
                isFinished,
              }}
            />

            {(!isFinished || donationCampaign) && (
              <div className="mt-5 flex flex-col">
                {!isFinished ? (
                  session ? (
                    <EventAttendeeStatusButton
                      event={event}
                      session={session}
                      mutateEventDrivers={mutateEventDrivers}
                      enableDonationCampaign
                    />
                  ) : (
                    <DonationCampaignCta campaign={donationCampaign}>
                      <LoginButton />
                    </DonationCampaignCta>
                  )
                ) : (
                  <DonationCampaignCta campaign={donationCampaign} />
                )}

                {!isFinished && (
                  <Countdown
                    events={[event]}
                    labelText="Event starts in"
                    onlyRenderIfCountdown={true}
                    countdownClassName="text-nowrap text-p-1! font-normal! text-neutral-blue-300!"
                    countDownContainerClassName={
                      'mt-4 flex h-5 items-center gap-1'
                    }
                  />
                )}
              </div>
            )}
          </div>

          {/* Main Img - Right side (on Desktop) */}
          <div className="relative col-span-3 col-start-6 hidden h-fit w-full overflow-hidden rounded-md bg-neutral-blue-100/20 desktop-xs:mt-auto desktop-xs:block desktop-sm:col-span-4 desktop-sm:col-start-7">
            <Image
              alt=""
              className="object-cover"
              src={eventImageUrl}
              height={1080}
              width={1920}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

function EventStartDate({
  startDateTime,
  finishDateTime,
  targetTimezone,
  isFinished,
}) {
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
  }, [])

  const isStarted =
    mounted && startDateTime ? dayjs().isAfter(dayjs(startDateTime)) : false

  return startDateTime && targetTimezone ? (
    !isFinished ? (
      <div className="flex w-full items-center gap-2 align-middle">
        <div className="relative flex size-6 min-w-6 items-center rounded-sm bg-neutral-blue-700 desktop-xs:size-[36px] desktop-xs:min-w-[36px]">
          <CalendarIcon2 className="mx-auto -mt-px size-3 stroke-neutral-blue-200 stroke-2 desktop-xs:size-4" />
        </div>
        <div className="flex w-full items-center justify-between gap-2 align-middle text-shades-0">
          <p className="text-p-1 font-semibold">
            {mounted ? (isStarted ? 'Started' : 'Starts') : 'Event time'}
          </p>
          <LocalizedDateTime
            datetime={startDateTime}
            timezone={targetTimezone}
            format="ddd D MMM • HH:mm z"
            className="truncate-1 text-p-1"
            fallback={<Skeleton className="h-5 w-40 rounded-sm" />}
          />
        </div>
      </div>
    ) : (
      <div className="flex w-full items-center gap-2 align-middle">
        <div className="relative flex size-6 min-w-6 items-center rounded-sm bg-neutral-blue-700 desktop-xs:size-[36px] desktop-xs:min-w-[36px]">
          <CalendarIcon2 className="mx-auto -mt-px size-3 stroke-neutral-blue-200 stroke-2 desktop-xs:size-4" />
        </div>
        <div className="flex w-full items-center justify-between gap-2 align-middle">
          <p className="text-p-1 font-semibold text-error-500">Finished</p>
          <LocalizedDateTime
            datetime={finishDateTime}
            timezone={targetTimezone}
            format="ddd D MMM • HH:mm z"
            className="truncate-1 text-p-1 text-shades-0"
            fallback={<Skeleton className="h-5 w-40 rounded-sm" />}
            emptyFallback={<span className="text-p-1 text-shades-0">N/a</span>}
          />
        </div>
      </div>
    )
  ) : (
    <div className="flex w-full items-center gap-2 align-middle">
      <div className="relative flex size-6 min-w-6 items-center rounded-sm bg-neutral-blue-700 desktop-xs:size-[36px] desktop-xs:min-w-[36px]">
        <CalendarIcon2 className="mx-auto -mt-px size-3 stroke-neutral-blue-200 stroke-2 desktop-xs:size-4" />
      </div>
      <div className="flex w-full items-center justify-between align-middle">
        <p className="text-p-1 font-semibold text-shades-0">Starts</p>
        <p className="text-p-1">N/a </p>
      </div>
    </div>
  )
}

function EventTrack({ event }) {
  const trackName = event?.track?.name || event?.trackMeta?.name
  const hasTrackmaniaMap = event?.metadata?.['trackmania-map']?.uid
  const trackmaniaMapUrl = hasTrackmaniaMap
    ? `https://trackmania.io/#/leaderboard/${event.metadata['trackmania-map'].uid}`
    : null

  return (
    <div className="flex w-full items-center gap-2 align-middle">
      <div className="relative flex size-6 min-w-6 items-center rounded-sm bg-neutral-blue-700 desktop-xs:size-[36px] desktop-xs:min-w-[36px]">
        <RouteIcon className="mx-auto size-3 stroke-neutral-blue-200 stroke-2 desktop-xs:size-4" />
      </div>
      <div className="flex w-full items-center justify-between gap-4 align-middle">
        <p className="text-p-1 font-semibold text-shades-0">Track</p>

        {trackName ? (
          trackmaniaMapUrl ? (
            <a
              href={trackmaniaMapUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="truncate-1 break-all text-p-1 text-blue-400 hover:text-blue-300 hover:underline"
            >
              {trackName}
            </a>
          ) : (
            <p className="truncate-1 break-all text-p-1">{trackName}</p>
          )
        ) : (
          <p className="truncate-1 break-all text-p-1">
            {event ? 'No track set' : 'Could not load track'}
          </p>
        )}
      </div>
    </div>
  )
}

function EventCarClass({ event }) {
  return (
    <div className="flex w-full items-center gap-2 align-middle">
      <div className="relative flex size-6 min-w-6 items-center rounded-sm bg-neutral-blue-700 desktop-xs:size-[36px] desktop-xs:min-w-[36px]">
        <CarIcon className="mx-auto size-3 stroke-neutral-blue-200 stroke-[1.5px] desktop-xs:size-4" />
      </div>
      <div className="flex w-full items-center justify-between gap-4 align-middle">
        <p className="text-p-1 font-semibold text-shades-0">Cars</p>

        <p className="truncate-1 break-all text-p-1">
          {event?.championship?.carClasses.length > 1
            ? 'Multi-Class'
            : event?.championship?.carClasses?.[0]?.carClass?.name
              ? event.championship.carClasses[0].carClass.name
              : 'N/A'}
        </p>
      </div>
    </div>
  )
}

function EventRegisteredDrivers({
  event,
  isFinished,
  eventDrivers,
  eventDriversLoading,
}) {
  const mainSession = useMemo(
    () => getFirstNonPracticeSession({ event }),
    [event],
  )

  const { attendees, gridPositions, gridPositionsLabel, isGridUnlimited } =
    useMemo(
      () =>
        getChampionshipEventDriverStats(
          { attendees: eventDriversLoading ? [] : eventDrivers },
          event.championship,
        ),
      [event, eventDrivers, eventDriversLoading],
    )

  return (
    <div className="flex w-full items-center gap-2 align-middle">
      <div className="relative flex size-6 min-w-6 items-center rounded-sm bg-neutral-blue-700 desktop-xs:size-[36px] desktop-xs:min-w-[36px]">
        <HelmetIcon className="mx-auto size-[14px] stroke-neutral-blue-200 stroke-2 desktop-xs:size-4" />
      </div>
      <div className="flex w-full items-center justify-between gap-2 align-middle">
        <p className="text-p-1 font-semibold text-shades-0">Drivers</p>
        {eventDriversLoading ? (
          <span className="flex! items-center gap-1 align-middle text-p-1">
            •
            <Skeleton className="mt-[3px] inline-block h-4 w-[80px] rounded-sm bg-neutral-blue-700" />
          </span>
        ) : !isFinished ? (
          <span
            className={cn('truncate-1 text-p-1 text-success-500', {
              'text-warning-500!':
                !isGridUnlimited &&
                attendees.length > gridPositions / 2 &&
                mainSession?.type !== 'hotlap',
            })}
          >
            • {attendees.length}{' '}
            {mainSession?.type !== 'hotlap' && `/ ${gridPositionsLabel}`}
          </span>
        ) : (
          <span className="truncate-1 text-p-1 text-shades-0">
            {attendees.length}{' '}
            {mainSession?.type !== 'hotlap' && `/ ${gridPositionsLabel}`}{' '}
            Attended
          </span>
        )}
      </div>
    </div>
  )
}
